/**
 * 🎯 BOOKING LOGGER - Автоматическое логирование изменений бронирований
 * Этап 6: Логирование и история изменений
 * 
 * @module bookingLogger
 * @description Утилиты для создания детальной истории изменений бронирований:
 * - Автоматическое логирование всех изменений полей
 * - Категоризация изменений по типам (гость, даты, финансы, VIP и т.д.)
 * - Человеко-читаемые названия полей и форматирование значений
 * - Батч-логирование множественных изменений
 * - Отслеживание изменений объектов (change tracker)
 * 
 * @example
 * ```typescript
 * // Логирование одного изменения
 * await logFieldChange(bookingId, 'guest_name', 'Иван', 'Петр', 'Исправление опечатки');
 * 
 * // Логирование нескольких изменений
 * await logMultipleChanges(bookingId, [
 *   { field: 'guest_name', oldValue: 'Иван', newValue: 'Петр' },
 *   { field: 'guest_phone', oldValue: '+79001234567', newValue: '+79007654321' }
 * ]);
 * 
 * // Отслеживание изменений объекта
 * const tracker = createChangeTracker(bookingId, originalBooking);
 * await tracker.logChanges(updatedBooking, 'Обновление данных гостя');
 * ```
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Типы категорий изменений для группировки и визуализации
 * 
 * @typedef {string} ChangeCategory
 * @property {'guest_info'} guest_info - Информация о госте (имя, телефон, email)
 * @property {'dates'} dates - Даты и время заезда/выезда
 * @property {'room'} room - Информация о номере и категории
 * @property {'financial'} financial - Финансовые данные (цены, скидки, тарифы)
 * @property {'services'} services - Дополнительные услуги
 * @property {'vip'} vip - VIP услуги (статус, трансфер, диета)
 * @property {'status'} status - Статус бронирования
 * @property {'communication'} communication - Коммуникация (WhatsApp, Email, счета)
 * @property {'other'} other - Прочие изменения
 */
export type ChangeCategory =
  | 'guest_info'      // Информация о госте
  | 'dates'           // Даты заезда/выезда
  | 'room'            // Информация о номере
  | 'financial'       // Финансовые данные
  | 'services'        // Услуги
  | 'vip'             // VIP услуги
  | 'status'          // Статус бронирования
  | 'communication'   // Коммуникация (WhatsApp, Email)
  | 'other';          // Другое

// Маппинг полей к категориям
const FIELD_CATEGORIES: Record<string, ChangeCategory> = {
  // Гость
  'guest_name': 'guest_info',
  'guest_phone': 'guest_info',
  'guest_email': 'guest_info',
  'guest_first_name': 'guest_info',
  'guest_last_name': 'guest_info',
  'guest_middle_name': 'guest_info',
  
  // Даты
  'check_in_date': 'dates',
  'check_out_date': 'dates',
  'check_in_time': 'dates',
  'check_out_time': 'dates',
  
  // Номер
  'room_type': 'room',
  'room_id': 'room',
  'room_number': 'room',
  
  // Финансы
  'total_amount': 'financial',
  'discount_percentage': 'financial',
  'discount_amount': 'financial',
  'rate_plan_id': 'financial',
  'base_price': 'financial',
  'manual_total_amount': 'financial',
  'price_locked': 'financial',
  
  // Услуги
  'services': 'services',
  
  // VIP
  'vip_status': 'vip',
  'transfer_needed': 'vip',
  'dietary_restrictions': 'vip',
  
  // Статус
  'status': 'status',
  
  // Коммуникация
  'whatsapp_sent_at': 'communication',
  'invoice_sent_at': 'communication',
  'invoice_number': 'communication',
  
  // Гости
  'adult_count': 'guest_info',
  'children_count': 'guest_info',
  'infants_count': 'guest_info',
  'number_of_guests': 'guest_info',
  'cribs_count': 'guest_info',
  
  // Другое
  'special_requests': 'other',
  'notes': 'other',
};

// Человеко-читаемые названия полей
const FIELD_LABELS: Record<string, string> = {
  'guest_name': 'Имя гостя',
  'guest_phone': 'Телефон',
  'guest_email': 'Email',
  'guest_first_name': 'Имя',
  'guest_last_name': 'Фамилия',
  'guest_middle_name': 'Отчество',
  'check_in_date': 'Дата заезда',
  'check_out_date': 'Дата выезда',
  'check_in_time': 'Время заезда',
  'check_out_time': 'Время выезда',
  'room_type': 'Категория номера',
  'room_id': 'Номер',
  'total_amount': 'Общая сумма',
  'discount_percentage': 'Скидка (%)',
  'discount_amount': 'Скидка (₽)',
  'rate_plan_id': 'Тарифный план',
  'base_price': 'Базовая цена',
  'vip_status': 'VIP статус',
  'transfer_needed': 'Трансфер',
  'dietary_restrictions': 'Диетические ограничения',
  'status': 'Статус бронирования',
  'adult_count': 'Взрослые',
  'children_count': 'Дети',
  'infants_count': 'Младенцы',
  'number_of_guests': 'Количество гостей',
  'cribs_count': 'Детские кроватки',
  'special_requests': 'Особые пожелания',
  'whatsapp_sent_at': 'WhatsApp отправлен',
  'invoice_sent_at': 'Счет отправлен',
  'invoice_number': 'Номер счета',
  'price_locked': 'Цена зафиксирована',
  'manual_total_amount': 'Ручная сумма',
};

/**
 * Логирует одно изменение поля бронирования в таблицу booking_history
 * 
 * @async
 * @function logFieldChange
 * @param {string} bookingId - ID бронирования
 * @param {string} fieldName - Название измененного поля
 * @param {any} oldValue - Старое значение поля
 * @param {any} newValue - Новое значение поля
 * @param {string} [reason] - Причина изменения (опционально)
 * @returns {Promise<void>}
 * 
 * @description
 * - Автоматически получает ID текущего пользователя из Supabase Auth
 * - Пропускает изменения где старое и новое значение идентичны
 * - Записывает изменение в таблицу booking_history с временной меткой
 * - Обрабатывает null/undefined значения
 * 
 * @remarks
 * Используйте для логирования единичных изменений. Для множественных изменений
 * более эффективно использовать logMultipleChanges() для батч-вставки.
 * 
 * @example
 * ```typescript
 * await logFieldChange(
 *   'booking-123',
 *   'guest_name',
 *   'Иванов Иван',
 *   'Петров Петр',
 *   'Исправление ошибки в имени гостя'
 * );
 * ```
 */
export const logFieldChange = async (
  bookingId: string,
  fieldName: string,
  oldValue: any,
  newValue: any,
  reason?: string
): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    
    // Преобразуем значения для сохранения
    const oldVal = oldValue !== undefined && oldValue !== null ? oldValue : null;
    const newVal = newValue !== undefined && newValue !== null ? newValue : null;
    
    // Пропускаем если значения одинаковые
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    
    const { error } = await supabase.from('booking_history').insert({
      booking_id: bookingId,
      field_changed: fieldName,
      old_value: oldVal,
      new_value: newVal,
      changed_by: userData?.user?.id || null,
      change_reason: reason || null,
      changed_at: new Date().toISOString(),
    });
    
    if (error) {
      console.error(`❌ Ошибка логирования изменения ${fieldName}:`, error);
    } else {
      console.log(`✅ Залогировано изменение: ${fieldName}`, { oldValue, newValue });
    }
  } catch (error) {
    console.error('❌ Ошибка логирования:', error);
  }
};

/**
 * Логирует изменение с контекстными данными бронирования
 * При каждом изменении сохраняет snapshot текущего состояния брони
 * 
 * @async
 * @function logChangeWithContext
 * @param {string} bookingId - ID бронирования
 * @param {string} fieldName - Название измененного поля
 * @param {any} oldValue - Старое значение поля
 * @param {any} newValue - Новое значение поля
 * @param {object} contextData - Контекстные данные брони (даты, суммы)
 * @param {string} [reason] - Причина изменения (опционально)
 * @returns {Promise<void>}
 * 
 * @description
 * - Записывает измененное поле + snapshot данных брони в new_value
 * - Включает даты заезда/выезда, total_amount, room_type в контекст
 * - Позволяет видеть полное состояние брони на момент изменения
 * 
 * @example
 * ```typescript
 * await logChangeWithContext(
 *   'booking-123',
 *   'status',
 *   'pending',
 *   'confirmed',
 *   {
 *     check_in_date: '2025-01-15',
 *     check_out_date: '2025-01-20',
 *     total_amount: 50000,
 *     room_type: 'Люкс'
 *   },
 *   'Подтверждение брони'
 * );
 * ```
 */
export const logChangeWithContext = async (
  bookingId: string,
  fieldName: string,
  oldValue: any,
  newValue: any,
  contextData: {
    check_in_date?: string;
    check_out_date?: string;
    total_amount?: number;
    room_type?: string;
    room_number?: string;
  },
  reason?: string
): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    
    // Преобразуем значения для сохранения
    const oldVal = oldValue !== undefined && oldValue !== null ? oldValue : null;
    const newVal = newValue !== undefined && newValue !== null ? newValue : null;
    
    // Пропускаем если значения одинаковые
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    
    // Создаем snapshot с контекстными данными
    const contextSnapshot = {
      field_value: newVal,
      booking_snapshot: {
        check_in_date: contextData.check_in_date,
        check_out_date: contextData.check_out_date,
        total_amount: contextData.total_amount,
        room_type: contextData.room_type,
        room_number: contextData.room_number
      }
    };
    
    const { error } = await supabase.from('booking_history').insert({
      booking_id: bookingId,
      field_changed: fieldName,
      old_value: oldVal,
      new_value: contextSnapshot,
      changed_by: userData?.user?.id || null,
      change_reason: reason || null,
      changed_at: new Date().toISOString(),
    });
    
    if (error) {
      console.error(`❌ Ошибка логирования изменения с контекстом ${fieldName}:`, error);
    } else {
      console.log(`✅ Залогировано изменение с контекстом: ${fieldName}`, { 
        oldValue, 
        newValue,
        context: contextData 
      });
    }
  } catch (error) {
    console.error('❌ Ошибка логирования с контекстом:', error);
  }
};

/**
 * Логирует множественные изменения с контекстными данными бронирования
 * 
 * @async
 * @function logMultipleChangesWithContext
 * @param {string} bookingId - ID бронирования
 * @param {Array} changes - Массив изменений
 * @param {object} contextData - Контекстные данные брони (даты, суммы)
 * @returns {Promise<void>}
 * 
 * @example
 * ```typescript
 * await logMultipleChangesWithContext('booking-123', [
 *   { field: 'status', oldValue: 'pending', newValue: 'confirmed' },
 *   { field: 'vip_status', oldValue: false, newValue: true }
 * ], {
 *   check_in_date: '2025-01-15',
 *   check_out_date: '2025-01-20',
 *   total_amount: 50000,
 *   room_type: 'Люкс'
 * });
 * ```
 */
export const logMultipleChangesWithContext = async (
  bookingId: string,
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    reason?: string;
  }>,
  contextData: {
    check_in_date?: string;
    check_out_date?: string;
    total_amount?: number;
    room_type?: string;
    room_number?: string;
  }
): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const now = new Date().toISOString();
    
    // Фильтруем изменения - только те где значения различаются
    const validChanges = changes.filter(change => {
      const oldVal = change.oldValue !== undefined && change.oldValue !== null ? change.oldValue : null;
      const newVal = change.newValue !== undefined && change.newValue !== null ? change.newValue : null;
      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    });
    
    if (validChanges.length === 0) {
      console.log('ℹ️ Нет изменений для логирования');
      return;
    }
    
    // Подготавливаем записи для вставки с контекстными данными
    const records = validChanges.map(change => {
      const newVal = change.newValue !== undefined && change.newValue !== null ? change.newValue : null;
      
      // Создаем snapshot с контекстными данными
      const contextSnapshot = {
        field_value: newVal,
        booking_snapshot: {
          check_in_date: contextData.check_in_date,
          check_out_date: contextData.check_out_date,
          total_amount: contextData.total_amount,
          room_type: contextData.room_type,
          room_number: contextData.room_number
        }
      };
      
      return {
        booking_id: bookingId,
        field_changed: change.field,
        old_value: change.oldValue !== undefined && change.oldValue !== null ? change.oldValue : null,
        new_value: contextSnapshot,
        changed_by: userId,
        change_reason: change.reason || null,
        changed_at: now,
      };
    });
    
    const { error } = await supabase.from('booking_history').insert(records);
    
    if (error) {
      console.error('❌ Ошибка логирования множественных изменений с контекстом:', error);
    } else {
      console.log(`✅ Залогировано изменений с контекстом: ${validChanges.length}`, {
        fields: validChanges.map(c => c.field),
        context: contextData
      });
    }
  } catch (error) {
    console.error('❌ Ошибка логирования множественных изменений с контекстом:', error);
  }
};

/**
 * Логирует множественные изменения за один раз (батч-логирование)
 * 
 * @async
 * @function logMultipleChanges
 * @param {string} bookingId - ID бронирования
 * @param {Array<{field: string; oldValue: any; newValue: any; reason?: string}>} changes - Массив изменений
 * @returns {Promise<void>}
 * 
 * @description
 * - Фильтрует изменения: записывает только те, где значения действительно различаются
 * - Выполняет одну батч-вставку всех изменений для оптимизации
 * - Автоматически получает ID текущего пользователя
 * - Устанавливает одинаковую временную метку для всех изменений в батче
 * 
 * @remarks
 * РЕКОМЕНДУЕТСЯ использовать вместо множественных вызовов logFieldChange()
 * для повышения производительности и атомарности операции.
 * 
 * @example
 * ```typescript
 * await logMultipleChanges('booking-123', [
 *   { field: 'guest_name', oldValue: 'Иван', newValue: 'Петр' },
 *   { field: 'guest_phone', oldValue: '+79001234567', newValue: '+79007654321' },
 *   { field: 'vip_status', oldValue: false, newValue: true, reason: 'Повышение до VIP' }
 * ]);
 * // Залогирует только 3 изменения одним запросом к БД
 * ```
 */
export const logMultipleChanges = async (
  bookingId: string,
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    reason?: string;
  }>
): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const now = new Date().toISOString();
    
    // Фильтруем изменения - только те где значения различаются
    const validChanges = changes.filter(change => {
      const oldVal = change.oldValue !== undefined && change.oldValue !== null ? change.oldValue : null;
      const newVal = change.newValue !== undefined && change.newValue !== null ? change.newValue : null;
      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    });
    
    if (validChanges.length === 0) {
      console.log('ℹ️ Нет изменений для логирования');
      return;
    }
    
    // Подготавливаем записи для вставки
    const records = validChanges.map(change => ({
      booking_id: bookingId,
      field_changed: change.field,
      old_value: change.oldValue !== undefined && change.oldValue !== null ? change.oldValue : null,
      new_value: change.newValue !== undefined && change.newValue !== null ? change.newValue : null,
      changed_by: userId,
      change_reason: change.reason || null,
      changed_at: now,
    }));
    
    const { error } = await supabase.from('booking_history').insert(records);
    
    if (error) {
      console.error('❌ Ошибка логирования множественных изменений:', error);
    } else {
      console.log(`✅ Залогировано изменений: ${validChanges.length}`, {
        fields: validChanges.map(c => c.field)
      });
    }
  } catch (error) {
    console.error('❌ Ошибка логирования множественных изменений:', error);
  }
};

/**
 * Создает автоматический трекер изменений для объекта бронирования
 * 
 * @function createChangeTracker
 * @param {string} bookingId - ID бронирования для логирования
 * @param {any} originalData - Исходные данные объекта для отслеживания
 * @returns {object} Объект с методами для работы с трекером
 * @returns {function} logChanges - Метод для проверки и логирования изменений
 * @returns {function} getCurrentState - Метод для получения текущего отслеживаемого состояния
 * 
 * @description
 * Создает объект-трекер, который:
 * - Хранит исходное состояние данных
 * - Сравнивает новые данные с отслеживаемым состоянием
 * - Автоматически логирует только измененные поля
 * - Обновляет отслеживаемое состояние после логирования
 * 
 * @remarks
 * Полезно для отслеживания изменений формы в реальном времени.
 * Автоматически фильтрует неизмененные поля.
 * 
 * @example
 * ```typescript
 * const tracker = createChangeTracker('booking-123', {
 *   guest_name: 'Иван Петров',
 *   guest_phone: '+79001234567',
 *   total_amount: 50000
 * });
 * 
 * // Позже, после изменений пользователем
 * await tracker.logChanges({
 *   guest_name: 'Иван Петров',        // Не изменилось
 *   guest_phone: '+79007654321',      // Изменилось - будет залогировано
 *   total_amount: 45000               // Изменилось - будет залогировано
 * }, 'Обновление данных клиентом');
 * 
 * // Получить текущее состояние
 * const currentState = tracker.getCurrentState();
 * ```
 */
export const createChangeTracker = (bookingId: string, originalData: any) => {
  const tracked = { ...originalData };
  
  return {
    /**
     * Проверяет и логирует изменения
     */
    logChanges: async (newData: any, reason?: string): Promise<void> => {
      const changes: Array<{
        field: string;
        oldValue: any;
        newValue: any;
        reason?: string;
      }> = [];
      
      // Проверяем каждое поле
      Object.keys(newData).forEach(key => {
        if (tracked[key] !== newData[key]) {
          changes.push({
            field: key,
            oldValue: tracked[key],
            newValue: newData[key],
            reason
          });
          
          // Обновляем отслеживаемое значение
          tracked[key] = newData[key];
        }
      });
      
      if (changes.length > 0) {
        await logMultipleChanges(bookingId, changes);
      }
    },
    
    /**
     * Получает текущее отслеживаемое состояние
     */
    getCurrentState: () => ({ ...tracked }),
  };
};

/**
 * Получает категорию изменения для заданного поля
 * 
 * @function getChangeCategory
 * @param {string} fieldName - Название поля
 * @returns {ChangeCategory} Категория изменения ('guest_info', 'dates', 'financial' и т.д.)
 * 
 * @description
 * Использует предопределенный маппинг FIELD_CATEGORIES для определения
 * категории поля. Если поле не найдено, возвращает 'other'.
 * 
 * @example
 * ```typescript
 * getChangeCategory('guest_name');     // Вернет 'guest_info'
 * getChangeCategory('total_amount');   // Вернет 'financial'
 * getChangeCategory('vip_status');     // Вернет 'vip'
 * getChangeCategory('unknown_field');  // Вернет 'other'
 * ```
 */
export const getChangeCategory = (fieldName: string): ChangeCategory => {
  return FIELD_CATEGORIES[fieldName] || 'other';
};

/**
 * Получает человеко-читаемое название поля на русском языке
 * 
 * @function getFieldLabel
 * @param {string} fieldName - Техническое название поля
 * @returns {string} Человеко-читаемое название на русском языке
 * 
 * @description
 * Использует предопределенный маппинг FIELD_LABELS для преобразования
 * технических названий полей в понятные пользователю строки.
 * Если маппинг не найден, возвращает исходное название поля.
 * 
 * @example
 * ```typescript
 * getFieldLabel('guest_name');          // Вернет 'Имя гостя'
 * getFieldLabel('total_amount');        // Вернет 'Общая сумма'
 * getFieldLabel('check_in_date');       // Вернет 'Дата заезда'
 * getFieldLabel('unknown_field');       // Вернет 'unknown_field'
 * ```
 */
export const getFieldLabel = (fieldName: string): string => {
  return FIELD_LABELS[fieldName] || fieldName;
};

/**
 * Форматирует значение поля для отображения в интерфейсе
 * 
 * @function formatChangeValue
 * @param {any} value - Значение для форматирования
 * @param {string} fieldName - Название поля (для контекстного форматирования)
 * @returns {string} Отформатированное значение в виде строки
 * 
 * @description
 * Применяет контекстное форматирование в зависимости от типа поля:
 * - Boolean → 'Да' / 'Нет'
 * - Финансовые поля → '50 000 ₽' (с разделителями и символом рубля)
 * - Проценты → '10%'
 * - Даты → '10.01.2025' (локальный формат)
 * - Время → '15:00'
 * - Объекты → JSON строка с форматированием
 * - null/undefined → 'Не указано'
 * 
 * @remarks
 * Поддерживает специальное форматирование для полей:
 * - vip_status, transfer_needed, price_locked (булевы)
 * - total_amount, discount_amount, base_price (валюта)
 * - discount_percentage (проценты)
 * - check_in_date, check_out_date (даты)
 * - check_in_time, check_out_time (время)
 * 
 * @example
 * ```typescript
 * formatChangeValue(true, 'vip_status');                    // 'Да'
 * formatChangeValue(50000, 'total_amount');                 // '50 000 ₽'
 * formatChangeValue(15, 'discount_percentage');             // '15%'
 * formatChangeValue('2025-01-10', 'check_in_date');         // '10.01.2025'
 * formatChangeValue(null, 'guest_email');                   // 'Не указано'
 * formatChangeValue({ key: 'value' }, 'metadata');          // '{\n  "key": "value"\n}'
 * ```
 */
export const formatChangeValue = (value: any, fieldName: string): string => {
  if (value === null || value === undefined) return 'Не указано';
  
  // 🔥 НОВОЕ: Если это объект с полем value (из триггера audit_booking_price_change)
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return formatChangeValue(value.value, fieldName);
  }
  
  // Специальная обработка для разных типов полей
  switch (fieldName) {
    case 'vip_status':
    case 'transfer_needed':
    case 'price_locked':
      return value ? 'Да' : 'Нет';
    
    case 'total_amount':
    case 'discount_amount':
    case 'base_price':
    case 'manual_total_amount':
      return `${Number(value).toLocaleString('ru-RU')} ₽`;
    
    case 'discount_percentage':
      return `${value}%`;
    
    case 'check_in_date':
    case 'check_out_date':
      return new Date(value).toLocaleDateString('ru-RU');
    
    case 'check_in_time':
    case 'check_out_time':
      return value;
    
    default:
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
  }
};
